import { Component, OnInit, Renderer2 } from '@angular/core'
import { CategoryDropdownComponent } from './category-dropdown/category-dropdown.component'
import { ICategory } from './typings'
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CategoriesService } from './categories.service';
import { EDevices, ResponsiveService } from '../services/responsive.service';
import { Destroy } from '../../core/destroyClass';
import { filter, forkJoin, map, takeUntil } from 'rxjs';
import { StickerModule, StickerOptions } from '@usitsdasdesign/dds-ng/sticker';
import { ButtonComponent, ButtonModule } from '@usitsdasdesign/dds-ng/button';
import { AccordionModule } from '@usitsdasdesign/dds-ng/accordion';

const SELECTED_CATEGORY = "SELECTED_CATEGORY";
const CATEGORIES = "CATEGORIES"
@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [
    CategoryDropdownComponent,
    ButtonModule,
    AccordionModule,
    StickerModule,
    RouterModule,
  ],
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.scss',
})
export class CategoriesComponent extends Destroy implements OnInit {
  protected EDevices = EDevices;

  categories: ICategory[];
  currentDevice: EDevices;

  stickerOptions: StickerOptions = {
    stickerWidth: 200,
    stickerPosition: 'bottom-left',
    stickerIndent: 15,
    stickerShift: 0,
    stickerIsDisabled: false,
    stickerIsResponsive: false,
    stickerIsOutsideClick: false,
    stickerCustomClass: "",
    stickerIsDynamic: true
  };

  constructor(
    private categoriesService: CategoriesService,
    private responsiveService: ResponsiveService,
    private renderer: Renderer2,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadData();
    this.currentDevice = this.responsiveService.getDevice();
    this.responsiveService.device$
      .pipe(takeUntil(this.destroy$))
      .subscribe((device) => (this.currentDevice = device));

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => this.checkSelectedCategory(event));
  }

  loadData() {
    const categoriesObservable = this.categoriesService.getCategoriesData()
    const subCategoriesObservable = this.categoriesService.getSubCategoriesData()

    forkJoin([categoriesObservable, subCategoriesObservable])
      .pipe(
        map(([categories, subcategories]) => {
          const sortedCategories = categories.sort((a, b) => a.order - b.order);
          return sortedCategories.map((category) => {
            const relatedSubcategories = subcategories
              .filter((sub) => sub.categoryId === category.id)
              .sort((a, b) => a.order - b.order)
              .map((sub) => ({
                id: sub.id,
                title: sub.subCategoryName,
                description: sub.subCategoryDescription,
                path: this.generatePath(sub.subCategoryName),
                order: sub.order,
                codeId: sub.codeId,
              }));
            return {
              id: category.id,
              title: category.categoryName,
              path: this.generatePath(category.categoryName),
              theme: category.theme,
              list: relatedSubcategories,
              order: category.order,
            } as ICategory;
          });
        }))
      .subscribe(
        {
          next: (transformedData: ICategory[]) => {
            this.categories = transformedData;
            localStorage.setItem(CATEGORIES, JSON.stringify(transformedData));
          },
          error: (err) => {
            console.error('Error loading data:', err);
          }
        });
  }

  generatePath(title: string): string {
    return title.toLowerCase()
      .trim()
      .replace(/[\s]+/g, '-')
      .replace(/[^\w\-]+/g, '');
  }

  gotTo(codeId: string): void {
    this.router.navigate(['codedetails/', codeId]);
  }

  onShownCategories(btn: ButtonComponent): void {
    this.renderer.addClass(btn.elementRef.nativeElement, 'active');
  }

  onHiddenCategories(btn: ButtonComponent): void {
    this.renderer.removeClass(btn.elementRef.nativeElement, 'active');
  }

  private checkSelectedCategory(event: any): void {
    if (!this.categories || this.categories.length === 0) {
      const storedCategories = localStorage.getItem(CATEGORIES);
      if (storedCategories) {
        try {
          this.categories = JSON.parse(storedCategories);
        } catch (error) {
          console.error(error);
          this.loadData();
        }
      } else {
        this.loadData();
        return;
      }
    }

    let activatedRoute = this.router.routerState.root;

    while (activatedRoute.firstChild) {
      activatedRoute = activatedRoute.firstChild;
    }

    const codeId = activatedRoute.snapshot.params['codeId'];
    const selectedCategory = this.findCategoryBySubCategoryCodeId(codeId)

    if (selectedCategory) {
      this.categoriesService.selectedCategory = selectedCategory;
      localStorage.setItem(SELECTED_CATEGORY, JSON.stringify(selectedCategory));
    } else {
      this.categoriesService.selectedCategory = undefined;
    }
  }
  findCategoryBySubCategoryCodeId(codeId: string): ICategory | null {
    if (!this.categories?.length || !codeId) {
      return null;
    }
    return this.categories.find(category =>
      category.list?.some(subCategory => subCategory.codeId == codeId)
    ) || null;
  };
}
